import { createRoute } from "@tanstack/solid-router";
import { lazy, Suspense } from "solid-js";
import { MUSCLE_GROUPS, type MuscleGroup } from "@gymlog/shared";
import { appRoute } from "./route";

const ExercisesPage = lazy(async () => {
  const m = await import("@/features/gym/components/exercises-page");
  return { default: m.ExercisesPage };
});

interface ExercisesSearch {
  /** Muscle group filter for deep links (e.g. from the summary). */
  group?: MuscleGroup;
  /** Search query shareable via URL (copyable/pasteable deep link). */
  q?: string;
  /**
   * Creation picker open: `?new=1` (exercise list) or `?new=new`
   * (custom exercise sub-form). Each step has its copyable URL.
   */
  new?: "1" | "new";
}

export const exercisesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/exercises",
  validateSearch: (search: Record<string, unknown>): ExercisesSearch => {
    const raw = search.group;
    const group = MUSCLE_GROUPS.find((g) => g === raw);
    // Omit empty fields to keep URLs clean (idempotency).
    const q = typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined;
    return {
      ...(group ? { group } : {}),
      ...(q ? { q } : {}),
      // CC-18: also accepts the NUMBER 1 (TanStack JSON-parses `?new=1` to number 1).
      ...(search.new === "1" || search.new === 1
        ? { new: "1" as const }
        : search.new === "new"
          ? { new: "new" as const }
          : {}),
    };
  },
  component: () => (
    <Suspense fallback={null}>
      <ExercisesPage />
    </Suspense>
  ),
});
